// Output formats (§6): detailed CSV, summary CSV, JSON/NDJSON and the plain-text report. Pure.

import type { Field, FieldResult, Overall, Profile, ProfileResult, WarningCode } from '../types';
import { FIELD_LEVEL, worst } from '../assemble';
import { csvLine } from './csv';
import { words } from '../text';

export type OutputFormat = 'csv' | 'summary-csv' | 'json' | 'ndjson' | 'text';
export const OUTPUT_FORMATS: OutputFormat[] = ['csv', 'summary-csv', 'json', 'ndjson', 'text'];

/** A page's results: one FieldResult per field present (or a single error FieldResult). */
export interface PageResult {
  url: string;
  error?: string;
  fields: FieldResult[];
}

export function pageOverall(page: PageResult): Overall {
  if (page.error) return 'error';
  return worst(page.fields.map(f => f.overall));
}

/** Inline notation for text outputs: `shown ✂ ⟦not shown⟧` (§6.3). */
export function inlineNotation(shown: string, notShown: string): string {
  return notShown ? `${shown} ✂ ⟦${notShown}⟧`.replace(/^ ✂/, '✂') : shown;
}

// ---------- detailed CSV ----------

export const DETAILED_COLUMNS = ['url', 'field', 'profile', 'status', 'shown_text', 'not_shown_text', 'cut_by', 'shown_pct', 'line_count', 'max_lines', 'room_left_px', 'warnings', 'profile_status', 'error'];

export function detailedCsvHeader(): string {
  return csvLine(DETAILED_COLUMNS);
}

export function detailedCsvRows(page: PageResult): string {
  if (page.error) return csvLine([page.url, '', '', 'error', '', '', '', '', '', '', '', '', '', page.error]);
  let out = '';
  for (const f of page.fields) {
    for (const r of f.results) {
      out += csvLine([page.url, f.field, r.profile, r.status, r.shownText, r.notShownText, r.cutBy, r.shownPct, r.lineCount, r.maxLines, r.roomLeftPx, r.warnings.join(';'), r.profileStatus, '']);
    }
  }
  return out;
}

// ---------- summary CSV ----------

export function summaryCsvHeader(profileIds: string[]): string {
  const cols = ['url', 'title_overall', 'description_overall'];
  for (const id of profileIds) cols.push(`${id}_title`, `${id}_description`);
  cols.push('warnings', 'error');
  return csvLine(cols);
}

export function summaryCsvRow(page: PageResult, profileIds: string[]): string {
  if (page.error) return csvLine([page.url, 'error', 'error', ...profileIds.flatMap(() => ['', '']), '', page.error]);
  const byField = new Map(page.fields.map(f => [f.field, f]));
  const cells: unknown[] = [page.url, byField.get('title')?.overall ?? '', byField.get('description')?.overall ?? ''];
  for (const id of profileIds) {
    for (const field of ['title', 'description'] as Field[]) cells.push(byField.get(field)?.results.find(r => r.profile === id)?.status ?? '');
  }
  const warns: string[] = [];
  for (const f of page.fields) for (const w of f.warnings) warns.push(`${f.field}:${w.code}`);
  cells.push(warns.join(';'), '');
  return csvLine(cells);
}

// ---------- JSON / NDJSON ----------

export function jsonItems(page: PageResult, opts: { lines?: boolean } = {}): object[] {
  if (page.error) return [{ url: page.url, error: page.error }];
  return page.fields.map(f => toJsonField(f, opts.lines !== false));
}

function toJsonField(f: FieldResult, withLines: boolean): object {
  return {
    url: f.url,
    field: f.field,
    original: f.original,
    overall: f.overall,
    googleCut: f.googleCut,
    results: f.results.map(r => {
      const o: Record<string, unknown> = {
        profile: r.profile,
        profileStatus: r.profileStatus,
        status: r.status,
        cutBy: r.cutBy,
      };
      if (withLines) {
        o.lines = r.lines;
        o.hiddenLines = r.hiddenLines;
      }
      o.shownText = r.shownText;
      o.notShownText = r.notShownText;
      o.shownPct = r.shownPct;
      o.lineCount = r.lineCount;
      o.maxLines = r.maxLines;
      o.roomLeftPx = r.roomLeftPx;
      if (r.bolded) o.bolded = true;
      if (r.fontFallback) o.fontFallback = r.fontFallback;
      o.warnings = r.warnings;
      return o;
    }),
    warnings: f.warnings,
  };
}

// ---------- plain-text report ----------

const HEAD: Record<Overall, string> = { fits: '✓ Fits', tight: '△ Tight', cut: '✂ Cut', error: '✗ Error' };

function elide(text: string, max = 10, keep = 4): string {
  const ws = words(text);
  if (ws.length <= max) return text;
  return `${ws.slice(0, keep).join(' ')} … ${ws.slice(-keep).join(' ')}`;
}

function profileLabel(p: Profile | undefined, id: string): string {
  const base = p?.shortLabel ?? p?.label ?? id;
  return p && p.status !== 'verified' ? base + '*' : base;
}

function countPhrase(n: number, total: number): string {
  if (n === total) return total === 1 ? 'on 1 device' : 'on all devices';
  if (n === 1) return 'on 1 device';
  return `on ${n} of ${total} devices`;
}

function roomRange(vals: number[]): string {
  const lo = Math.round(Math.min(...vals));
  const hi = Math.round(Math.max(...vals));
  return lo === hi ? `${lo}px room left` : `${lo}–${hi}px room left`;
}

function profileWarningNotes(f: FieldResult, r: ProfileResult): string[] {
  const notes: string[] = [];
  for (const code of r.warnings) {
    if (FIELD_LEVEL.has(code) || code === 'TIGHT') continue;
    const w = f.warnings.find(x => x.code === code);
    if (code === 'DANGLING_END') {
      const last = words(r.shownText).at(-1) ?? '';
      const lastChar = Array.from(r.shownText).at(-1) ?? '';
      const d = w?.detail?.split(', ').find(x => x.toLowerCase() === last.toLowerCase() || x === lastChar) ?? last;
      notes.push(`⚠ DANGLING_END ("${d}")`);
    } else if (code === 'LOW_SHOWN') notes.push(`⚠ LOW_SHOWN (${r.shownPct}% shown)`);
    else notes.push(`⚠ ${code}`);
  }
  return notes;
}

function reasonLine(f: FieldResult, r: ProfileResult, keptEnd: number): string {
  const gc = f.googleCut;
  const google = f.field === 'title'
    ? `cut by Google (~${Math.round(gc.widthPx ?? 0)}px vs ${gc.limitPx}px limit)`
    : `cut by Google (${gc.chars} chars vs ~${gc.charCap} cap)`;
  const hiddenByLines = f.original.slice(r.shownEnd, keptEnd).trim();
  const lines = `"${elide(hiddenByLines, 6, 3)}" hidden by line limit (${r.lineCount} lines, ${r.maxLines} shown)`;
  switch (r.cutBy) {
    case 'google': return google;
    case 'lines': return lines;
    case 'both': return `${google} + ${lines}`;
    default: return '';
  }
}

/** Kept end of the original text after Google's cut (sent minus the ellipsis). */
function keptEndOf(f: FieldResult, ellipsis: string): number {
  if (!f.googleCut.cut) return f.original.length;
  return f.googleCut.sent.length - ellipsis.length;
}

export interface TextReportOptions {
  profiles: Map<string, Profile>;
  ellipsis?: string;
}

/** Text for one field: headline plus groups of profiles with the same visible outcome. */
export function textReportField(f: FieldResult, opts: TextReportOptions): string {
  const ellipsis = opts.ellipsis ?? ' ...';
  const total = f.results.length;
  const n = f.results.filter(r => r.status === f.overall).length;
  let head = `${f.field.toUpperCase()} — ${HEAD[f.overall]} ${countPhrase(n, total)}`;
  for (const w of f.warnings) {
    if (!FIELD_LEVEL.has(w.code)) continue;
    head += ` · ⚠ ${w.code}${w.detail ? ` ("${w.detail}")` : ''}`;
  }
  const keptEnd = keptEndOf(f, ellipsis);
  const groups = new Map<string, ProfileResult[]>();
  for (const r of f.results) {
    const pw = r.warnings.filter(c => !FIELD_LEVEL.has(c)).join(',');
    const linesHid = r.cutBy === 'lines' || r.cutBy === 'both';
    const key = [r.shownEnd, r.cutBy, r.status, pw, linesHid ? `${r.lineCount}/${r.maxLines}` : ''].join('|');
    const g = groups.get(key);
    if (g) g.push(r);
    else groups.set(key, [r]);
  }
  const entries = [...groups.values()].map(members => {
    const label = groups.size === 1 ? 'All devices' : members.map(r => profileLabel(opts.profiles.get(r.profile), r.profile)).join(', ');
    const r = members[0];
    const text = inlineNotation(elide(r.shownText), elide(r.notShownText));
    let note: string;
    if (r.status === 'cut') note = reasonLine(f, r, keptEnd);
    else note = `${r.status === 'tight' ? 'tight' : 'fits'} · ${roomRange(members.map(m => m.roomLeftPx))}`;
    const warns = profileWarningNotes(f, r);
    if (warns.length) note += ` · ${warns.join(' · ')}`;
    return { label, text, note };
  });
  const longest = Math.max(...entries.map(e => e.label.length));
  let out = head + '\n';
  if (longest > 36) {
    for (const e of entries) out += `  ${e.label}\n      ${e.text}\n      ${e.note}\n`;
  } else {
    const pad = longest + 3;
    for (const e of entries) {
      out += `  ${e.label.padEnd(pad)}${e.text}\n`;
      out += `  ${' '.repeat(pad)}${e.note}\n`;
    }
  }
  return out;
}

export function textReportPage(page: PageResult, opts: TextReportOptions): string {
  if (page.error) return `${page.url}\nERROR — ${page.error}\n`;
  return `${page.url}\n` + page.fields.map(f => textReportField(f, opts)).join('\n');
}

export const UNVERIFIED_FOOTER = '* unverified profile (assumed or experimental); results may differ from Google.';

export function textReportFooter(profiles: Profile[]): string {
  return profiles.some(p => p.status !== 'verified') ? `\n${UNVERIFIED_FOOTER}\n` : '';
}

/** "Copy as text" for one profile result (never elided). */
export function copyAsText(f: FieldResult, r: ProfileResult): string {
  return inlineNotation(r.shownText, r.notShownText);
}

export function warningCodesForCsv(codes: WarningCode[]): string {
  return codes.join(';');
}
